import { Weekday } from "../types";
import { DateTime } from "luxon";

/**
 * Every weekday, in order
 */
export const DAYS: Weekday[] = [
  Weekday.Sunday,
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday
];

export type DateLike = Date | string | number;
export type TimeZone = string;

export function ensureDate(value: DateLike): Date {
  if (value instanceof Date) {
    return value;
  }
  return new Date(value);
}

export function epoch(dateLike: DateLike) {
  return new Date(dateLike).getTime();
}

export function copyDate(value: DateLike): Date {
  return new Date(epoch(value));
}

export function getDayOfWeek(time: Date, timeZone: TimeZone): Weekday {
  if (timeZone == null) {
    throw new Error("Timezone Missing");
  }

  const weekday = new Date(time)
    // This en-US is pretty important for some date checks ☠️
    // We use local date string with the given timezone to control for time,
    // then en-US to ensure consistent weekday names for comparison.
    .toLocaleDateString(["en-US"], { timeZone, weekday: "long" })
    .toLowerCase();

  return weekday as Weekday;
}

export function dayRange(start: Date, repeat: number): Date[] {
  const days = [];

  for (let i = 0; i <= repeat; i++) {
    days.push(dateShift(start, { days: i }));
  }

  return days;
}

/**
 * Returns an array of dates for every day of the week starting from
 * the given date.
 */
export function weekdayRange(date: Date): Date[] {
  return dayRange(date, 6);
}

export function isPast(date: Date, now: Date): boolean {
  return ensureDate(date).getTime() < ensureDate(now).getTime();
}

export function isFuture(date: Date, now: Date): boolean {
  return ensureDate(date).getTime() > ensureDate(now).getTime();
}

export function timeDistance(date1: Date, date2: Date): [number, number, number] {
  // get total seconds between the times
  date1 = ensureDate(date1);
  date2 = ensureDate(date2);

  let delta = (date2.getTime() - date1.getTime()) / 1000;

  // calculate (and subtract) whole days
  const days = Math.floor(delta / 86400);
  delta -= days * 86400;

  // calculate (and subtract) whole hours
  const hours = Math.floor(delta / 3600) % 24;
  delta -= hours * 3600;

  // calculate (and subtract) whole minutes
  const minutes = Math.floor(delta / 60) % 60;
  delta -= minutes * 60;

  // what's left is seconds
  const seconds = delta % 60;

  return [Math.floor(hours), Math.floor(minutes), Math.round(seconds)];
}

export function timeSegment(segment: number): string {
  return `${segment || 0}`.padStart(2, "0");
}

export type DateShiftOptions = {
  years?: number;
  days?: number;
  months?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
};

export function dateShift(now: DateLike, options: DateShiftOptions): Date {
  const date = copyDate(now);

  if (options.days) {
    date.setDate(date.getDate() + options.days);
  }

  if (options.months) {
    date.setMonth(date.getMonth() + options.months);
  }

  if (options.years) {
    date.setFullYear(date.getFullYear() + options.years);
  }

  if (options.hours) {
    date.setHours(date.getHours() + options.hours);
  }

  if (options.minutes) {
    date.setMinutes(date.getMinutes() + options.minutes);
  }

  if (options.seconds) {
    date.setSeconds(date.getSeconds() + options.seconds);
  }

  return date;
}

export function isSameDate(a: DateLike, b: DateLike, timeZone: TimeZone) {
  if (timeZone == null) {
    throw new Error("Timezone Missing");
  }

  return (
    ensureDate(a).toLocaleDateString(["en-US"], { timeZone }) ==
    ensureDate(b).toLocaleDateString(["en-US"], { timeZone })
  );
}

/**
 * Seconds in milliseconds
 */
export function seconds(n: number): number {
  return n * 1000;
}

/**
 * Minutes in milliseconds
 */
export function minutes(n: number): number {
  return seconds(n * 60);
}

/**
 * Hours in milliseconds
 */
export function hours(n: number): number {
  return minutes(n * 60);
}

export function getStartOfDay(now: DateLike, timeZone: TimeZone) {
  return DateTime.fromJSDate(ensureDate(now), { zone: timeZone }).startOf("day").toJSDate();
}

export function getEndOfDay(now: DateLike, timeZone: TimeZone): Date {
  return DateTime.fromJSDate(ensureDate(now), { zone: timeZone }).endOf("day").toJSDate();
}
